import type { Metadata } from "next";
import Link from "next/link";
import { ZODIAC, zodiacBySlug, zodiacFromYear } from "@/lib/zodiac";

export const metadata: Metadata = {
  title: "12 Con Giáp — Tử Vi Số",
  description: "Tra cứu tính cách, sự nghiệp, tình yêu của 12 con giáp theo năm sinh.",
};

type Props = { searchParams: Promise<{ slug?: string; year?: string }> };

export default async function ZodiacPage({ searchParams }: Props) {
  const sp = await searchParams;
  const year = sp.year ? parseInt(sp.year, 10) : null;
  const fromYear = year && year > 1900 && year < 2200 ? zodiacFromYear(year) : null;
  const selected = sp.slug ? zodiacBySlug(sp.slug) : fromYear;

  return (
    <div className="bg-bg-base min-h-screen">
      <div className="mx-auto max-w-5xl px-4 py-8 lg:py-12">
        <div className="mb-8 text-center">
          <h1 className="font-display text-ink-primary text-3xl font-semibold lg:text-4xl">
            12 Con Giáp
          </h1>
          <p className="text-ink-secondary mt-2 text-sm">
            Tính cách, sự nghiệp, tình yêu — chọn theo tuổi hoặc năm sinh.
          </p>
        </div>

        <form
          method="GET"
          className="border-stroke-subtle bg-bg-elevated/40 mx-auto mb-8 flex max-w-md items-end gap-2 rounded-xl border p-4"
        >
          <div className="flex-1">
            <label className="text-ink-muted block text-xs">Năm sinh dương lịch</label>
            <input
              type="number"
              name="year"
              defaultValue={year ?? ""}
              placeholder="vd: 1992"
              min={1900}
              max={2100}
              className="bg-bg-inset border-stroke-default text-ink-primary focus:border-brand-gold mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="bg-brand-gold text-bg-base rounded-md px-4 py-2 text-sm font-semibold"
          >
            Tra
          </button>
        </form>

        {/* 12-card grid */}
        <div className="mb-10 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6 lg:gap-3">
          {ZODIAC.map((z) => (
            <Link
              key={z.slug}
              href={`/12-con-giap?slug=${z.slug}`}
              className={`border-stroke-subtle bg-bg-elevated/40 hover:border-brand-gold/60 flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition ${
                selected?.slug === z.slug ? "border-brand-gold bg-bg-elevated" : ""
              }`}
            >
              <div className="font-display text-ink-primary text-xl font-semibold">{z.name}</div>
              <div className="text-ink-muted text-[10px]">{z.english}</div>
              <div className="text-brand-gold/70 text-[10px]">{z.element}</div>
            </Link>
          ))}
        </div>

        {selected ? (
          <article className="border-stroke-subtle bg-bg-elevated/40 rounded-2xl border p-6 lg:p-8">
            <div className="border-stroke-subtle border-b pb-4">
              <div className="text-brand-gold text-xs tracking-[0.2em] uppercase">
                {selected.english} · {selected.element} · {selected.yinYang}
              </div>
              <h2 className="font-display text-ink-primary mt-1 text-3xl font-semibold lg:text-4xl">
                Tuổi {selected.name}
              </h2>
              <p className="text-ink-secondary mt-3 text-base leading-relaxed">
                {selected.description}
              </p>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <Section title="Tính cách">
                <p className="text-ink-secondary text-sm leading-relaxed">
                  {selected.traits.join(" · ")}
                </p>
              </Section>

              <Section title="Tam Hợp">
                <p className="text-ink-secondary text-sm">{selected.tamHop}</p>
              </Section>

              <Section title="Điểm mạnh">
                <ul className="text-ink-secondary list-disc space-y-1 pl-5 text-sm">
                  {selected.strengths.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </Section>

              <Section title="Điểm yếu">
                <ul className="text-ink-secondary list-disc space-y-1 pl-5 text-sm">
                  {selected.weaknesses.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </Section>

              <Section title="Sự nghiệp">
                <p className="text-ink-secondary text-sm leading-relaxed">{selected.career}</p>
              </Section>

              <Section title="Tình yêu">
                <p className="text-ink-secondary text-sm leading-relaxed">{selected.love}</p>
              </Section>
            </div>
          </article>
        ) : null}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="font-display text-brand-gold mb-2 text-sm font-semibold tracking-wider uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}
